'use client'

import type { ReactNode } from 'react'
import { useTranslations } from '@/lib/next-shim'

import { AddressDetailsForm, type AddressDetails } from '@/shared/ui/address/AddressDetailsForm'

import type { ManualAddress } from '../model/types'
import { confirmButtonClassName } from '../lib/classNames'

type ManualAddressFormProps = {
  value: ManualAddress
  onChange: (value: ManualAddress) => void
  details: AddressDetails
  onDetailsChange: (details: AddressDetails) => void
  onConfirm: () => void
  /** Slot for the optional "save this address" checkbox. */
  children?: ReactNode
}

export function ManualAddressForm({ value, onChange, details, onDetailsChange, onConfirm, children }: ManualAddressFormProps) {
  const t = useTranslations('address')

  const labelClassName = 'block text-sm font-medium leading-6 text-gray-900'
  const inputClassName = 'bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5'

  return (
    <div className="space-y-3">
      <div>
        <label className={labelClassName}>{t('street')}</label>
        <input type="text" value={value.street} onChange={(e) => onChange({ ...value, street: e.target.value })} className={inputClassName} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelClassName}>{t('city')}</label>
          <input type="text" value={value.city} onChange={(e) => onChange({ ...value, city: e.target.value })} className={inputClassName} />
        </div>
        <div>
          <label className={labelClassName}>{t('postalCode')}</label>
          <input type="text" value={value.postalCode} onChange={(e) => onChange({ ...value, postalCode: e.target.value })} className={inputClassName} />
        </div>
      </div>
      <div>
        <label className={labelClassName}>{t('country')}</label>
        <input type="text" value={value.country} onChange={(e) => onChange({ ...value, country: e.target.value })} className={inputClassName} />
      </div>

      {/* Access details, inline (no separate confirm step) */}
      <div>
        <p className="mb-2 text-sm font-medium text-gray-700">{t('accessDetails')}</p>
        <AddressDetailsForm value={details} onChange={onDetailsChange} />
      </div>

      {children}

      <button type="button" onClick={onConfirm} disabled={!value.street || !value.city} className={confirmButtonClassName}>
        {t('confirmAddress')}
      </button>
    </div>
  )
}
